import _find from 'lodash/find';
import sortBy from 'lodash/sortBy';
import { fetcher } from '../saga/fetcher.saga';
import { compactForIpadView } from './ipadView';

/**
 * Vue iPad : relit une liste dans les tables sources (la carte telle qu'elle
 * est sur la caisse). Même forme que la liste brouillon correspondante.
 * @param {'product'|'supplement'|'productMenu'} entity
 * @param {String} idMenu
 * @returns {Promise<Object[]>}
 */
export function fetchIpadSource(entity, idMenu) {
	return fetcher('ipadSource', 'GET', true, null, '/' + entity, {
		id_menu: idMenu,
	}).then(json => {
		if (!json || !Array.isArray(json.result)) {
			throw new Error('ipadSource: réponse inattendue');
		}
		return json.result;
	});
}

/**
 * Couleur de fond d'une case : id_color résolu dans les couleurs, sinon la
 * couleur déjà résolue par le serveur (menus), sinon gris.
 */
export function colorOf(item, colors) {
	const color = _find(colors || [], c => c.id === item.id_color);
	if (color) return color.color;
	if (typeof item.color === 'string' && item.color[0] === '#') return item.color;
	return '#5a5a5a';
}

/**
 * Cases de la grille dans l'ordre des poids, une case vide pour chaque poids
 * manquant (comme les grilles du back-office), puis la règle de la caisse :
 * indisponibles retirés (si elle les cache), ceux qui suivent remontent, pas
 * de cases vides en fin.
 * @param {Object[]} items chacun avec un `weight`
 * @param {Boolean} [hideDisabled] la caisse cache les indisponibles
 * @returns {Object[]}
 */
export function toIpadCells(items, hideDisabled = true) {
	const sorted = sortBy(items, item => Number(item.weight));
	const cells = [];
	sorted.forEach(item => {
		const weight = Number(item.weight);
		while (cells.length < weight) {
			cells.push({ isSpace: true });
		}
		cells.push(item);
	});
	return compactForIpadView(cells, hideDisabled);
}

/**
 * Sections à afficher pour une grille, à partir de la liste de la caisse.
 * @param {'product'|'supplement'|'productMenu'|'menuLevels'} source
 * @param {Object[]} list réponse de fetchIpadSource
 * @param {String} [parentId] catégorie, catégorie de suppléments ou menu
 * @param {Boolean} [hideDisabled] la caisse cache les indisponibles
 * @returns {{ title: String|null, cells: Object[] }[]} vide si rien à montrer
 */
export function buildIpadSections(source, list, parentId, hideDisabled = true) {
	let sections = [];
	if (source === 'product') {
		const items = list
			.filter(p => (p.categories || []).includes(parentId))
			.map(p => {
				const link = _find(p.product_category_weight, w => w.idCategory === parentId);
				return { ...p, weight: link ? link.productWeight : p.weight };
			});
		sections = [{ title: null, cells: toIpadCells(items, hideDisabled) }];
	} else if (source === 'supplement') {
		// Sur la caisse, les suppléments s'enchaînent par poids, sans case vide,
		// et les indisponibles restent affichés (grisés).
		const items = list.filter(s => s.category_id === parentId);
		sections = [
			{
				title: items.length ? items[0].category_name || '' : null,
				titleColorId: items.length ? items[0].category_id_color : null,
				layout: 'supplement',
				cells: sortBy(items, s => Number(s.weight)),
			},
		];
	} else if (source === 'productMenu') {
		// La liste des menus n'a pas d'espaces : on garde l'ordre des poids.
		const items = sortBy(list, m => Number(m.weight)).map((m, index) => ({
			...m,
			weight: index,
		}));
		sections = [{ title: null, cells: toIpadCells(items, hideDisabled) }];
	} else if (source === 'menuLevels') {
		const menu = _find(list, m => m.id === parentId);
		sections = menu
			? sortBy(menu.menuLevels || [], l => Number(l.weight)).map(level => ({
					title: level.name || '',
					cells: toIpadCells(level.products || [], hideDisabled),
			  }))
			: [];
	}
	return sections.filter(section => section.cells.length > 0);
}

/**
 * Couleur #rrggbb en rgba, pour le fond pâle des suppléments.
 */
export function withAlpha(hex, alpha) {
	const m = /^#?([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i.exec(hex || '');
	if (!m) return `rgba(90, 90, 90, ${alpha})`;
	const [r, g, b] = m.slice(1).map(x => parseInt(x, 16));
	return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

/**
 * Prix tel que la caisse l'écrit : « 7,90 », rien à 0, « Prix libre ».
 */
export function formatIpadPrice(item, freePriceLabel) {
	if (Number(item.is_free_price)) return freePriceLabel;
	const price = parseFloat(item.price);
	if (!price) return '';
	return price.toFixed(2).replace('.', ',');
}
